import { handleSetAlbums } from '@/storage/modules/albums/reducer'
import { AlbumsDataProps } from '@/types/albumsProps'
import { IoTrash } from 'react-icons/io5'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import colors from 'tailwindcss/colors'
import { FormAlbum } from '../FormAlbum'
import { useModal } from '@/hooks/useModal'
import { useToast } from '@/hooks/useToast'
import { api } from '@/services/api'
import { EmptyState } from '../EmptyState'
import {
  EMPTY_PAGINATION_META,
  type PaginatedResponseProps,
  type PaginationMetaProps,
  normalizePaginationMeta,
} from '@/types/paginationProps'
import { ALBUMS_UPDATED_EVENT } from '@/services/albumsEvents'

export function Albums() {
  const { openModal } = useModal()
  const { showToast } = useToast()
  const dispatch = useDispatch()
  const [albumsList, setAlbumsList] = useState<AlbumsDataProps[]>([])
  const [paginationMeta, setPaginationMeta] = useState<PaginationMetaProps>(
    EMPTY_PAGINATION_META,
  )
  const [isLoadingInitial, setIsLoadingInitial] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const isLoadingMoreRef = useRef(false)
  const paginationMetaRef = useRef<PaginationMetaProps>(EMPTY_PAGINATION_META)
  const canLoadMoreRef = useRef(false)

  const canLoadMore =
    paginationMeta.lastPage > 0 &&
    paginationMeta.currentPage < paginationMeta.lastPage

  const handleFetchAlbumsPage = async (page: number) => {
    const response = await api.get<PaginatedResponseProps<AlbumsDataProps>>(
      '/albums',
      {
        params: {
          page,
        },
      },
    )

    const pagination = normalizePaginationMeta(
      response.data.meta,
      response.data.data.length,
    )

    return {
      albums: response.data.data,
      pagination,
    }
  }

  const syncAlbumsStore = async () => {
    const responseAlbums = await api
      .get('/albums', {
        params: {
          page: 1,
        },
      })
      .then((res) => res.data.data as AlbumsDataProps[])

    dispatch(handleSetAlbums({ albums: responseAlbums }))
  }

  const handleLoadFirstPage = async () => {
    try {
      setIsLoadingInitial(true)

      const response = await handleFetchAlbumsPage(1)
      setAlbumsList(response.albums)
      setPaginationMeta(response.pagination)
      await syncAlbumsStore()
    } catch (error) {
      showToast('Error fetching albums', {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsLoadingInitial(false)
    }
  }

  const handleRemoveAlbum = async (albumId: string) => {
    try {
      await api.delete(`/albums/${albumId}`)
      await syncAlbumsStore()
      setAlbumsList((prev) => prev.filter((album) => album.id !== albumId))
      setPaginationMeta((prev) => ({
        ...prev,
        totalItems: Math.max(0, prev.totalItems - 1),
      }))

      showToast('Album removed successfully', {
        type: 'success',
        theme: 'light',
      })
    } catch (error) {
      showToast('Error removing album', {
        type: 'error',
        theme: 'light',
      })
    }
  }

  useEffect(() => {
    handleLoadFirstPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    paginationMetaRef.current = paginationMeta
    canLoadMoreRef.current = canLoadMore
  }, [canLoadMore, paginationMeta])

  useEffect(() => {
    const loadMoreAlbums = async () => {
      const nextPage = paginationMetaRef.current.currentPage + 1

      if (
        !canLoadMoreRef.current ||
        nextPage <= 0 ||
        isLoadingMoreRef.current
      ) {
        return
      }

      try {
        isLoadingMoreRef.current = true
        setIsLoadingMore(true)

        const response = await handleFetchAlbumsPage(nextPage)
        paginationMetaRef.current = response.pagination
        canLoadMoreRef.current =
          response.pagination.lastPage > 0 &&
          response.pagination.currentPage < response.pagination.lastPage

        setAlbumsList((prev) => [...prev, ...response.albums])
        setPaginationMeta(response.pagination)
      } catch (error) {
        showToast('Error fetching more albums', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        isLoadingMoreRef.current = false
        setIsLoadingMore(false)
      }
    }

    const handleScroll = () => {
      if (!canLoadMoreRef.current || isLoadingMoreRef.current) {
        return
      }

      const viewportHeight = window.innerHeight
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const fullHeight = document.documentElement.scrollHeight

      if (!fullHeight) {
        return
      }

      const scrollProgress = (scrollTop + viewportHeight) / fullHeight

      if (scrollProgress >= 0.8) {
        loadMoreAlbums()
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [showToast])

  useEffect(() => {
    const handleAlbumsUpdated = () => {
      handleLoadFirstPage()
    }

    window.addEventListener(ALBUMS_UPDATED_EVENT, handleAlbumsUpdated)

    return () => {
      window.removeEventListener(ALBUMS_UPDATED_EVENT, handleAlbumsUpdated)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <div className="mt-6 flex items-center justify-between gap-3 md:flex-col md:items-start">
        <p className="text-sm text-gray-500">
          Showing {albumsList.length} of {paginationMeta.totalItems} albums
        </p>
      </div>

      {albumsList.length === 0 && !isLoadingInitial && (
        <EmptyState
          title="No albums yet"
          description="Create the first album to organize tracks."
        />
      )}
      {albumsList.map((album: AlbumsDataProps) => (
        <div key={album.id} className="relative mt-8">
          <button
            title="Remove"
            className="absolute -right-1.5 -top-1.5 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-red-600 bg-red-600 shadow-sm transition hover:bg-red-700"
            onClick={() => {
              openModal({
                textConfirm: 'Delete',
                description: 'Do you really want to delete the album?',
                title: 'Attention',
                confirm() {
                  handleRemoveAlbum(album.id)
                },
              })
            }}
          >
            <IoTrash size={14} color={colors.white} />
          </button>

          <button
            className="bg-white rounded-2xl p-7 top-5 flex gap-12 justify-between items-center border hover:border-gray-300 w-full"
            onClick={() => {
              openModal({
                small: true,
                children: <FormAlbum isExist={album} />,
              })
            }}
          >
            <p className="font-semibold text-gray-500">{album.title}</p>
            <p className="text-sm text-gray-400">{album.slug}</p>
          </button>
        </div>
      ))}

      {(isLoadingInitial || isLoadingMore) && (
        <div className="mt-8 flex items-center justify-center gap-3 text-sm text-gray-500">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />
          <p>
            {isLoadingInitial ? 'Loading albums...' : 'Loading more albums...'}
          </p>
        </div>
      )}
    </div>
  )
}
